//#region import libraries
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Virtuoso } from 'react-virtuoso';

// custom components
import TimelineHourRow from "../timeline-components/timeline-hour-row";

// timeline job components
import TimeColumn from "./timeline-jobs/time-column";

// redux
import { useSelector, useDispatch } from "react-redux";
import { selectPostedBookingsStatus, setShowAddPassenger } from "_redux/slices/timeline/timeline-data";
import { resetPostedBooking, resetAdditionalPassengers } from '_redux/slices/prm-bookings/bookings';
import { selectThirtyMinLayout, selectTimelineIndex } from "_redux/slices/timeline/timeline-control";
import { selectIata } from '_redux/slices/main-screen/user';

// constants
import { EmployeeInformationColumnWidth, TimelineColumnWidth, TimelineColumnWidthRaw } from "../timeline-constants";

// helper functions
import { getCurrentHalfHourSlot } from '_utils/functions';

//#endregion

const TimelineJobs = (props) => {

  //#region redux
  const dispatch = useDispatch()

  const timeIndex = useSelector(selectTimelineIndex);
  const postedBookingStatus = useSelector(selectPostedBookingsStatus)         // status of post request
  const thirtyMinLayout = useSelector(selectThirtyMinLayout);
  const iata = useSelector(selectIata);
  const disableVirtuosoForIata = iata === 'JER';

  //#endregion

  //#region state
  // Track current time slot to trigger re-renders when time changes
  const [currentTimeSlot, setCurrentTimeSlot] = useState(getCurrentHalfHourSlot(thirtyMinLayout));
  //#endregion

  //#region useEffect
  // reset add passenger modal on mount
  useEffect(() => {
    dispatch(setShowAddPassenger(null));
    dispatch(resetPostedBooking())
    dispatch(resetAdditionalPassengers())
  }, []);

  // monitor patch status
  useEffect(() => {
    if (postedBookingStatus == 'succeeded') {
      // show success modal
      dispatch(setShowAddPassenger(null));
      dispatch(resetAdditionalPassengers());
    }
  }, [postedBookingStatus]);

  // Update current time slot every minute to move red line to correct column
  useEffect(() => {
    const updateCurrentTime = () => {
      setCurrentTimeSlot(getCurrentHalfHourSlot(thirtyMinLayout));
    };

    // Update immediately
    updateCurrentTime();

    // Update every minute
    const intervalId = setInterval(updateCurrentTime, 60000);

    return () => clearInterval(intervalId);
  }, []);

  //#endregion

  // Calculate which columns are visible based on scroll position
  const containerRef = useRef(null);

  // Memoize visible columns to avoid recreating on every render
  const visibleColumns = useMemo(() => {

    const currentHalfHourSlot = currentTimeSlot;
    const currentIndex = timeIndex.indexOf(currentHalfHourSlot);

    // Render all columns with proper past/future indication
    return timeIndex.map((hour, index) => {

      // A column is in the past if its index is less than the current time's index
      const isInPast = currentIndex !== -1 && index < currentIndex;

      const currentHour = `${(new Date().getHours()).toString().padStart(2, "0")}:00`;
      // if current time minutes less than 30 make the cuurent minutes 00 else 30
      const currentMinutes = new Date().getMinutes() < 30 ? "00" : "30";

      const showRedLine = thirtyMinLayout
        ? `${currentHour?.split(":")?.[0]}:${currentMinutes}` === hour
        : currentHour === hour;

      return (
        <TimeColumn
          key={hour}
          time={hour}
          showRedLine={showRedLine}
          isInPast={isInPast}
          index={index}
          handleGetBookings={props?.handleGetBookings}
          disableVirtuosoForIata={disableVirtuosoForIata}
        />
      );
    });
  }, [timeIndex, currentTimeSlot])

  // scroll listener
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleScroll = () => {
      const atEnd =
        el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;

      if (atEnd) {
        console.log("Reached END of horizontal scroll");
      }
    };

    el.addEventListener("scroll", handleScroll);

    return () => {
      el.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* timeline hour row */}
      <TimelineHourRow />

      {/* map each time interval */}
      <div ref={containerRef} style={{ display: "flex", flex: 1, minHeight: disableVirtuosoForIata ? "fit-content" : 0, padding: 0 }}>

        {/* employee column spacer */}
        <div style={{
          minWidth: TimelineColumnWidthRaw,
          maxWidth: TimelineColumnWidthRaw,
          left: 0,
          zIndex: 1,
          backgroundColor: 'white',
        }} />

        {/* time columns */}
        {visibleColumns}

      </div>

    </div>
  );
};

export default React.memo(TimelineJobs);

